import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import usePageStore from './pageStore';
import useDataStore from '../Data/dataStore';
import PageSection from './PageSection';
import HomeStorySection from '../Sections/HomeStorySection';
import DemoFullName from '../DemoFullName';
import Footer from '../Footer/Footer';
import { API, FOOTER_ID, footerKey } from '../globalUtils';
import ProductsSection from '../Sections/ProductsSection';
import BespokeSolutionsSection from '../Sections/BespokeSolutionsSection';
import HomePricingSection from '../Sections/HomePricingSection';
import HomeSectorsSection from '../Sections/HomeSectorsSection';
import HomeNewsSection from '../Sections/HomeNewsSection';
import ConnectSection from '../Sections/ConnectSection';
import LicensesHeaderSection from '../Sections/LicensesHeaderSection';
import LicensesSection from '../Sections/LicensesSection';
import AboutHeaderSection from '../Sections/AboutHeaderSection';
import TeamSection from '../Sections/TeamSection';
import SolutionsHeader from '../Sections/SolutionsHeaderSection';
import SolutionsContentSection from '../Sections/SolutionsContentSection';
import AccordionSection from '../Sections/AccordionSection';
import SectorsCards from '../Sections/SectorsCards';
import ProductDetailsSection from '../Sections/ProductDetailsSection';
import EventsSection from '../Sections/EventsSection';
import LegalsSection from '../Sections/LegalsSection';
import SEOSection from '../Sections/SEOSection';
import BlogSection from '../Sections/BlogSection';
const PageContent = () => {
    const { shownPage, shownSection } = usePageStore();
    const [_, setRender] = useState(0);
    const lastSection = useRef(shownSection);
    useEffect(() => {
        if (shownSection >= 0 && lastSection.current !== shownSection) {
            lastSection.current = shownSection;
            setRender((x) => x + 1);
        }
    }, [shownSection]);
    const pageRawContent = useDataStore((state) => state.data[shownPage]?.page_sections);
    // Blog-only re-render trigger. A blog page has no `page_sections`, so the
    // subscription above reads `undefined` both before and after its post
    // arrives and never wakes this component -- and the post itself is read
    // below with a non-reactive getState(), so a post landing after the first
    // render stayed invisible. On every other page this selector is `undefined`
    // and stays `undefined`, so nothing outside a blog post is affected.
    useDataStore((state) => state.data[shownPage]?.isBlogPage ? state.data[shownPage]?.blog : undefined);
    let pageContent = pageRawContent;
    const isBlog = useDataStore.getState().data[shownPage]?.isBlogPage;
    if (isBlog) {
        const cont = useDataStore.getState().data[shownPage]?.blog;
        if (cont) {
            pageContent = [{ ...cont, type: 'blog' }];
        }
        const blogTemplate = useDataStore.getState().data[API.blog];
        if (blogTemplate) {
            pageContent = [...(pageContent ?? []), ...blogTemplate.page_sections];
        }
    }
    const snap = useDataStore((state) => state.data[shownPage]?.snap);
    const forceSnap = !!pageContent?.[shownSection]?.force_snap;
    const definedFooter = pageContent?.length &&
        pageContent[pageContent.length - 1].type === footerKey;
    return (_jsxs(_Fragment, { children: [!isBlog && _jsx(SEOSection, { pageContent: pageContent }), _jsxs("div", { className: "page-wrapper", children: [(pageContent ?? []).map((content, idx) => {
                        const sectionId = `${shownPage}${idx}`;
                        const flexibleHeight = content.flexible_height;
                        return (_jsx(PageSection, { id: sectionId, idx: idx, forceSnap: forceSnap, flexibleHeight: flexibleHeight, children: [content.type].map((type) => {
                                if (!type || type === 'none')
                                    return null;
                                const isAllowed = lastSection.current === idx;
                                switch (type) {
                                    case 'home_text':
                                        return (_jsx(HomeStorySection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'products_tile_view':
                                        return (_jsx(ProductsSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'bespoke_solutions':
                                        return (_jsx(BespokeSolutionsSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'home_pricing':
                                        return (_jsx(HomePricingSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'home_sectors':
                                        return (_jsx(HomeSectorsSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'home_news':
                                        return (_jsx(HomeNewsSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'connect':
                                    case 'footer':
                                        return (_jsx(ConnectSection, { content: content, idx: idx, allowOpen: isAllowed, hasFooter: type === 'footer' }, sectionId));
                                    case 'licensing_title':
                                        return (_jsx(LicensesHeaderSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'licenses':
                                        return (_jsx(LicensesSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'about_header':
                                        return (_jsx(AboutHeaderSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'team':
                                        return (_jsx(TeamSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'solutions_header':
                                        return (_jsx(SolutionsHeader, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'solutions_content':
                                        return (_jsx(SolutionsContentSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'accordion':
                                        return (_jsx(AccordionSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'sectors_cards':
                                        return (_jsx(SectorsCards, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'product_details':
                                        return (_jsx(ProductDetailsSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'events':
                                        return (_jsx(EventsSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'legals':
                                        return (_jsx(LegalsSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    case 'blog':
                                        return (_jsx(BlogSection, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                    default:
                                        return (_jsx(DemoFullName, { content: content, idx: idx, allowOpen: isAllowed }, sectionId));
                                }
                            }) }, sectionId));
                    }), pageContent?.length && !definedFooter && (_jsx(PageSection, { id: FOOTER_ID, idx: pageContent?.length ?? 0, forceSnap: snap, flexibleHeight: !snap, children: _jsx(Footer, { flexibleHeight: !snap, idx: pageContent?.length ?? 0, animated: snap }) }, shownPage))] })] }));
};
export default PageContent;
